import * as Sentry from "@sentry/react"
import { v4 as uuidv4 } from "uuid"

import { getClientInfo } from "./clientInfo"

import { base64urlDecode, base64urlEncode } from "../../utils/base64/base64"
import { getJSTISOString } from "../../utils/date"

// this function should be called without await (Fire-and-forget)
export const sendEvent = async (
	userID: string,
	eventName: string,
	parameters: Record<string, any>,
	eventID?: string,
	location?: string
) => {
	const processedParameters = convertDatesToJSTString(parameters)
	const currentLocation = location || window.location.href
	const currentEventID = eventID || uuidv4()
	const isTest = process.env.REACT_APP_IS_PROD !== "true"
	const body = {
		event_id: currentEventID,
		is_test: isTest,
		user_id: userID,
		event_name: eventName,
		location: currentLocation,
		params: processedParameters,
		// デバイス種別・画面サイズ等は全イベント共通なので、呼び出し側の指定なしで毎回付ける
		...getClientInfo(),
	}

	try {
		const response = await fetch(
			`${process.env.REACT_APP_FE_EVENT_POST_STORE_EVENT_API_ADDR}/go.fe_event.v1.FeEventService/StoreEvent`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(body),
			}
		)
		if (!response.ok) {
			throw new Error(`Failed to send event: ${response.statusText}`)
		}
	} catch (error) {
		// sentry に送る
		Sentry.captureException(error)
	}
}

// URLから指定したクエリパラメータを取得し、null を除外してイベントパラメータを組み立てる
export const getUrlEventParams = (keys: string[]): Record<string, string> => {
	const urlParams = new URLSearchParams(window.location.search)
	const result: Record<string, string> = {}
	for (const key of keys) {
		const value = urlParams.get(key)
		if (value) result[key] = value
	}
	return result
}

// base64url として復号できる形かどうか。壊れた URL で atob が throw して
// 請求書画面ごと落ちるのを防ぐためのガード
const isDecodableBase64Url = (value: string): boolean =>
	/^[A-Za-z0-9\-_]*={0,2}$/.test(value) &&
	value.replace(/=+$/, "").length % 4 !== 1

// URLから base64url エンコードされたクエリパラメータ (請求書URLの claim_count) を取得してデコードする。
// キー自体も base64url エンコードされているため、キーもエンコードしてから引く
export const getEncodedUrlEventParam = (key: string): string | undefined => {
	const urlParams = new URLSearchParams(window.location.search)
	const value = urlParams.get(base64urlEncode(key))
	if (!value || !isDecodableBase64Url(value)) {
		return undefined
	}
	return base64urlDecode(value)
}

// Date型をJST形式の文字列に変換する再帰的な関数
export const convertDatesToJSTString = (obj: any): any => {
	if (obj instanceof Date) {
		return getJSTISOString(obj)
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => convertDatesToJSTString(item))
	}

	if (obj !== null && typeof obj === "object") {
		const result: Record<string, any> = {}
		for (const [key, value] of Object.entries(obj)) {
			result[key] = convertDatesToJSTString(value)
		}
		return result
	}

	return obj
}
